// ABOUTME: Locale-aware numeric parser turning human-typed numbers ("4.500,00", "€ 1 234,50")
// ABOUTME: into a JS number, using the locale's own separators to break the "4.500" ambiguity.

// Mirror of server/src/core/utils/parseLocaleNumber.ts (there is no shared package between the
// two workspaces): that file is the reference implementation and the two must stay in sync.

type Separators = { group: string; decimal: string };

const separatorsCache = new Map<string, Separators>();

/**
 * Reads the grouping and decimal marks of a locale from Intl instead of hardcoding '.' / ','.
 * Falls back to the en convention when the locale tag is not usable.
 */
function getLocaleSeparators(locale: string): Separators {
    const cached = separatorsCache.get(locale);
    if (cached) {
        return cached;
    }

    let separators: Separators = { group: ',', decimal: '.' };
    try {
        const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
        separators = {
            group: parts.find(p => p.type === 'group')?.value ?? ',',
            decimal: parts.find(p => p.type === 'decimal')?.value ?? '.',
        };
    } catch {
        // unusable locale tag: keep the en defaults
    }

    separatorsCache.set(locale, separators);
    return separators;
}

const countOccurrences = (text: string, char: string) => text.split(char).length - 1;

/**
 * Parses a human-entered number written in any of the formats users actually paste
 * ("4.500,00", "4,500.00", "€ 1 234,50", "12,5%", "(1.234,56)") into a JS number.
 *
 * Returns null when the input is empty or cannot be read as a single number, so callers
 * can reject the value instead of storing NaN.
 */
export function parseLocaleNumber(raw: string | number, locale: string): number | null {
    if (typeof raw === 'number') {
        return Number.isFinite(raw) ? raw : null;
    }
    if (typeof raw !== 'string') {
        return null;
    }

    let text = raw.trim();
    if (text === '') {
        return null;
    }

    // Currency symbols, whitespace and the Swiss apostrophe thousands mark carry no numeric
    // meaning. \s already covers NBSP and narrow NBSP, the marks fr/ch locales group with.
    text = text
        .replace(/[€$£¥]/g, '')
        .replace(/\s/g, '')
        .replace(/['’]/g, '');

    // "12,5%" is the number 12.5; the x100 conversion, where it applies, belongs to the caller.
    text = text.replace(/%$/, '');

    // Accounting notation: (1.234,56) is -1234.56
    let negative = false;
    if (/^\(.*\)$/.test(text)) {
        negative = true;
        text = text.slice(1, -1);
    }
    if (text.startsWith('-')) {
        negative = !negative;
        text = text.slice(1);
    } else if (text.startsWith('+')) {
        text = text.slice(1);
    }

    if (text === '') {
        return null;
    }

    const { group } = getLocaleSeparators(locale);
    const dots = countOccurrences(text, '.');
    const commas = countOccurrences(text, ',');

    let normalized: string;
    if (dots > 0 && commas > 0) {
        // Both marks present: the last one is the decimal separator, the other is grouping.
        const decimalMark = text.lastIndexOf('.') > text.lastIndexOf(',') ? '.' : ',';
        const groupMark = decimalMark === '.' ? ',' : '.';
        normalized = text.split(groupMark).join('').replace(decimalMark, '.');
    } else if (dots + commas > 1) {
        // A single mark repeated can only be grouping ("1.234.567").
        normalized = text.split(dots > 0 ? '.' : ',').join('');
    } else if (dots + commas === 1) {
        const mark = dots > 0 ? '.' : ',';
        const [head, tail] = text.split(mark);
        // The irreducible ambiguity: "4.500" is 4500 under it and 4.5 under en. Only the
        // locale's own grouping mark, followed by exactly three digits, reads as grouping.
        normalized = mark === group && /^\d{3}$/.test(tail) ? `${head}${tail}` : `${head}.${tail}`;
    } else {
        normalized = text;
    }

    if (!/^\d+(\.\d+)?$/.test(normalized)) {
        return null;
    }

    const value = Number(normalized);
    return negative ? -value : value;
}
